// Monitoring Manager for application metrics and health checks
export class MonitoringManager {
  static initialize() {
    this.metrics = {
      httpRequests: 0,
      httpErrors: 0,
      calculationRequests: 0,
      calculationErrors: 0,
      paymentRequests: 0,
      paymentFailures: 0,
      subscriptionRequests: 0,
      subscriptionFailures: 0,
      activeUsers: 0,
      databaseConnections: 0,
      memoryUsage: 0,
      responseTime: 0
    };
    
    this.healthChecks = {
      database: false,
      payment: false,
      authentication: false,
      analytics: false
    };
    
    this.startTime = Date.now();
    this.isInitialized = true;
    
    // Start periodic health checks
    this.startHealthChecks();
    
    // MonitoringManager initialized
  }

  // Track HTTP request
  static trackHttpRequest(method, path, statusCode, responseTime) {
    this.metrics.httpRequests++;
    
    if (statusCode >= 400) {
      this.metrics.httpErrors++;
    }
    
    this.metrics.responseTime = responseTime;
    
    // Track to analytics if available
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('http_request', {
        method,
        path,
        status_code: statusCode,
        response_time: responseTime
      });
    }
  }

  // Track calculation request
  static trackCalculationRequest(calculatorType, success, processingTime) {
    this.metrics.calculationRequests++;
    
    if (!success) {
      this.metrics.calculationErrors++;
    }
    
    // Track to analytics if available
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('calculation_request', {
        calculator_type: calculatorType,
        success,
        processing_time: processingTime
      });
    }
  }

  // Track generic events
  static trackEvent(eventName, data = {}) {
    // Track to analytics if available
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent(eventName, data);
    }
    
    // Update relevant metrics based on event type
    switch (eventName) {
      case 'app_startup':
        this.metrics.activeUsers++;
        break;
      case 'calculation_start':
        this.metrics.calculationRequests++;
        break;
      case 'calculation_success':
        // Already tracked in calculation_start
        break;
      case 'calculation_error':
        this.metrics.calculationErrors++;
        break;
      case 'calculation_validation_error':
        this.metrics.calculationErrors++;
        break;
      case 'input_change':
        // Track user interaction
        break;
      default:
        // Generic event tracking
        break;
    }
  }

  // Track payment request
  static trackPaymentRequest(amount, currency, success) {
    this.metrics.paymentRequests++;
    
    if (!success) {
      this.metrics.paymentFailures++;
    }
    
    // Track to analytics if available
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('payment_request', {
        amount,
        currency,
        success
      });
    }
  }

  // Track subscription request
  static trackSubscriptionRequest(planId, success) {
    this.metrics.subscriptionRequests++;
    
    if (!success) {
      this.metrics.subscriptionFailures++;
    }
    
    // Track to analytics if available
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('subscription_request', {
        plan_id: planId,
        success
      });
    }
  }

  // Update active users count
  static updateActiveUsers(count) {
    this.metrics.activeUsers = count;
  }

  // Update database connections
  static updateDatabaseConnections(count) {
    this.metrics.databaseConnections = count;
  }

  // Update memory usage
  static updateMemoryUsage(usage) {
    this.metrics.memoryUsage = usage;
  }

  // Start health checks
  static startHealthChecks() {
    // Check database health every 30 seconds
    setInterval(() => {
      this.checkDatabaseHealth();
    }, 30000);

    // Check payment system health every 60 seconds
    setInterval(() => {
      this.checkPaymentHealth();
    }, 60000);

    // Check authentication health every 60 seconds
    setInterval(() => {
      this.checkAuthenticationHealth();
    }, 60000);

    // Check analytics health every 60 seconds
    setInterval(() => {
      this.checkAnalyticsHealth();
    }, 60000);
  }

  // Check database health
  static async checkDatabaseHealth() {
    try {
      if (window.DatabaseManager) {
        const health = await window.DatabaseManager.healthCheck();
        this.healthChecks.database = health.status === 'healthy';
      }
    } catch (error) {
      this.healthChecks.database = false;
      // Database health check failed - handled silently
    }
  }

  // Check payment system health
  static async checkPaymentHealth() {
    try {
      if (window.PaymentManager) {
        const stripe = window.PaymentManager.getStripe();
        this.healthChecks.payment = !!stripe;
      }
    } catch (error) {
      this.healthChecks.payment = false;
      // Payment health check failed - handled silently
    }
  }

  // Check authentication health
  static async checkAuthenticationHealth() {
    try {
      if (window.AuthManager) {
        const isInitialized = window.AuthManager.isInitialized;
        this.healthChecks.authentication = isInitialized;
      }
    } catch (error) {
      this.healthChecks.authentication = false;
      // Authentication health check failed - handled silently
    }
  }

  // Check analytics health
  static async checkAnalyticsHealth() {
    try {
      if (window.AnalyticsManager) {
        const events = window.AnalyticsManager.getEvents(null, 1);
        this.healthChecks.analytics = events.length >= 0;
      }
    } catch (error) {
      this.healthChecks.analytics = false;
      // Analytics health check failed - handled silently
    }
  }

  // Get current metrics
  static getMetrics() {
    const uptime = Date.now() - this.startTime;
    
    return {
      ...this.metrics,
      uptime,
      uptimeFormatted: this.formatUptime(uptime),
      timestamp: new Date().toISOString()
    };
  }

  // Get health status
  static getHealthStatus() {
    const allHealthy = Object.values(this.healthChecks).every(status => status);
    
    return {
      status: allHealthy ? 'healthy' : 'unhealthy',
      checks: this.healthChecks,
      timestamp: new Date().toISOString()
    };
  }

  // Get system performance metrics
  static getPerformanceMetrics() {
    const metrics = this.getMetrics();
    
    return {
      errorRate: metrics.httpRequests > 0 ? (metrics.httpErrors / metrics.httpRequests) * 100 : 0,
      calculationSuccessRate: metrics.calculationRequests > 0 ? 
        ((metrics.calculationRequests - metrics.calculationErrors) / metrics.calculationRequests) * 100 : 0,
      paymentSuccessRate: metrics.paymentRequests > 0 ? 
        ((metrics.paymentRequests - metrics.paymentFailures) / metrics.paymentRequests) * 100 : 0,
      subscriptionSuccessRate: metrics.subscriptionRequests > 0 ? 
        ((metrics.subscriptionRequests - metrics.subscriptionFailures) / metrics.subscriptionRequests) * 100 : 0,
      averageResponseTime: metrics.responseTime,
      activeUsers: metrics.activeUsers,
      memoryUsage: metrics.memoryUsage
    };
  }

  // Format uptime
  static formatUptime(milliseconds) {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Export metrics for Prometheus
  static exportPrometheusMetrics() {
    const metrics = this.getMetrics();
    const health = this.getHealthStatus();
    
    let prometheusMetrics = `# HELP smsf_calculator_http_requests_total Total HTTP requests
# TYPE smsf_calculator_http_requests_total counter
smsf_calculator_http_requests_total ${metrics.httpRequests}

# HELP smsf_calculator_http_errors_total Total HTTP errors
# TYPE smsf_calculator_http_errors_total counter
smsf_calculator_http_errors_total ${metrics.httpErrors}

# HELP smsf_calculator_calculation_requests_total Total calculation requests
# TYPE smsf_calculator_calculation_requests_total counter
smsf_calculator_calculation_requests_total ${metrics.calculationRequests}

# HELP smsf_calculator_calculation_errors_total Total calculation errors
# TYPE smsf_calculator_calculation_errors_total counter
smsf_calculator_calculation_errors_total ${metrics.calculationErrors}

# HELP smsf_calculator_payment_requests_total Total payment requests
# TYPE smsf_calculator_payment_requests_total counter
smsf_calculator_payment_requests_total ${metrics.paymentRequests}

# HELP smsf_calculator_payment_failures_total Total payment failures
# TYPE smsf_calculator_payment_failures_total counter
smsf_calculator_payment_failures_total ${metrics.paymentFailures}

# HELP smsf_calculator_subscription_requests_total Total subscription requests
# TYPE smsf_calculator_subscription_requests_total counter
smsf_calculator_subscription_requests_total ${metrics.subscriptionRequests}

# HELP smsf_calculator_subscription_failures_total Total subscription failures
# TYPE smsf_calculator_subscription_failures_total counter
smsf_calculator_subscription_failures_total ${metrics.subscriptionFailures}

# HELP smsf_calculator_active_users Current active users
# TYPE smsf_calculator_active_users gauge
smsf_calculator_active_users ${metrics.activeUsers}

# HELP smsf_calculator_response_time_seconds Average response time
# TYPE smsf_calculator_response_time_seconds gauge
smsf_calculator_response_time_seconds ${metrics.responseTime}

# HELP smsf_calculator_uptime_seconds Application uptime
# TYPE smsf_calculator_uptime_seconds gauge
smsf_calculator_uptime_seconds ${metrics.uptime / 1000}

# HELP smsf_calculator_health_status Application health status (1=healthy, 0=unhealthy)
# TYPE smsf_calculator_health_status gauge
smsf_calculator_health_status ${health.status === 'healthy' ? 1 : 0}
`;

    return prometheusMetrics;
  }

  // Reset metrics
  static resetMetrics() {
    this.metrics = {
      httpRequests: 0,
      httpErrors: 0,
      calculationRequests: 0,
      calculationErrors: 0,
      paymentRequests: 0,
      paymentFailures: 0,
      subscriptionRequests: 0,
      subscriptionFailures: 0,
      activeUsers: 0,
      databaseConnections: 0,
      memoryUsage: 0,
      responseTime: 0
    };
    
    // Metrics reset
  }

  // Get monitoring dashboard data
  static getDashboardData() {
    return {
      metrics: this.getMetrics(),
      health: this.getHealthStatus(),
      performance: this.getPerformanceMetrics(),
      prometheus: this.exportPrometheusMetrics()
    };
  }
}